// AIアシスタントのチャット添付画像をクライアント側で圧縮するユーティリティ。
// 長辺を 1568px に収め JPEG へ再エンコードして、無駄なトークン・帯域を送らないようにする。
// GIF は圧縮できずアップロードサイズが大きくなるため、対応形式から除外している。

// 全プロバイダの自動縮小前サイズにほぼ一致する長辺（無駄なトークンを送らないため）
const MAX_DIMENSION = 1568;
// 再エンコード後の目安サイズ。これ以下になるまで品質を段階的に下げる
const TARGET_BYTES = 300 * 1024;

// canvas で再エンコードできる（＝アルファ以外の再現に問題がない）入力フォーマット
const RECOMPRESS_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

// 添付を受け付ける画像形式か（拡張子文字列ではなく MIME タイプで判定）。
// D&D・ペースト・ファイル選択の各経路で「非対応が混在したら全件中止」の判定に使う。
export function isAcceptedChatImageType(type: string): boolean {
  return (RECOMPRESS_TYPES as readonly string[]).includes(type);
}

// OSにMIMEタイプ登録が無い環境（Windows + HEIF拡張機能未導入等）では file.type が空文字になり、
// MIME判定だけでは画像と判定できないことがある拡張子。判定のフォールバックとしてのみ使う。
const IMAGE_LIKE_EXTENSIONS_WITH_UNRELIABLE_MIME = ['.heic', '.heif'] as const;

/**
 * ドロップされたファイルを画像バケットへ振り分けるべきか（MIMEタイプ、フォールバックで拡張子も見る）。
 * gif/heic等 isAcceptedChatImageType では非対応の画像形式も画像バケットに振り分け、
 * useChatImageAttachments 側の「対応していない画像形式です」という分かりやすいエラーに乗せる
 * （テキストファイル候補として扱いバックエンドの「読み込めません」に流すより適切なため）。
 *
 * 拡張子フォールバックは file.type が空文字（MIME判定不能）の場合のみ使う。
 * 明示的な非画像MIME（例: text/plain）が申告されているなら、拡張子だけで画像と判定してはいけない。
 */
export function looksLikeChatImageFile(file: File): boolean {
  if (file.type.startsWith('image/')) return true;
  if (file.type !== '') return false;
  const lower = file.name.toLowerCase();
  return IMAGE_LIKE_EXTENSIONS_WITH_UNRELIABLE_MIME.some((ext) => lower.endsWith(ext));
}

type ChatImageCompressionResult =
  | { type: 'ok'; blob: Blob; mediaType: string }
  | { type: 'error'; key: 'viewModel.aiChat.imageUnsupportedFormat'; params: { filename: string } }
  | { type: 'error'; key: 'viewModel.aiChat.imageDecodeFailed'; params: { filename: string } };

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to decode image'));
    };
    image.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

// 長辺が max を超える場合のみ、アスペクト比を保って縮小した幅・高さを返す
function fitWithin(width: number, height: number, max: number): { width: number; height: number } {
  if (width <= max && height <= max) {
    return { width, height };
  }
  const scale = max / Math.max(width, height);
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

async function encodeToJpeg(image: HTMLImageElement): Promise<Blob | null> {
  const { width, height } = fitWithin(image.naturalWidth, image.naturalHeight, MAX_DIMENSION);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // 透過画像を JPEG 化すると背景が黒くなるため、白で塗ってから描画する
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(image, 0, 0, width, height);

  let quality = 0.85;
  let blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  while (blob && blob.size > TARGET_BYTES && quality > 0.4) {
    quality -= 0.15;
    blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  }
  return blob;
}

/**
 * チャット添付画像を圧縮する。
 * - png/jpeg/webp: 長辺 1568px・JPEG へ再エンコードし、目安 300KB 以下まで品質を下げる
 * - それ以外（GIF / HEIC 等）: 非対応としてエラーを返す
 */
export async function compressChatImage(file: File): Promise<ChatImageCompressionResult> {
  if (!(RECOMPRESS_TYPES as readonly string[]).includes(file.type)) {
    // GIF は圧縮できずサイズが大きくなるため非対応。HEIC 等も canvas でデコードできないブラウザが多く非対応
    return { type: 'error', key: 'viewModel.aiChat.imageUnsupportedFormat', params: { filename: file.name } };
  }

  let image: HTMLImageElement;
  try {
    image = await loadImageFromFile(file);
  } catch {
    return { type: 'error', key: 'viewModel.aiChat.imageDecodeFailed', params: { filename: file.name } };
  }

  const blob = await encodeToJpeg(image);
  if (!blob) {
    return { type: 'error', key: 'viewModel.aiChat.imageDecodeFailed', params: { filename: file.name } };
  }
  return { type: 'ok', blob, mediaType: 'image/jpeg' };
}
